/**
 * Сверка версии бюджета с файлом Excel до тенге (ПМ-01, приложение Б ТЗ). Журнал исправлений итогов файла
 * «было → стало» записан при загрузке версии; сверка читает файл и показывает строки и недели, которые
 * расходятся с порталом. Новая версия при сверке не создаётся.
 */
import type { BudgetCorrection, BudgetCorrectionsView, BudgetReconciliation } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSearch } from 'lucide-react';
import { useRef } from 'react';
import { api } from '../../lib/api';
import { date, tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

const signed = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${tenge(Math.abs(v))}`;

function Corrections({ items }: { items: BudgetCorrection[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map((c, i) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: список не меняется, недели в нём могут повторяться
          key={i}
          className="rounded-md border border-border px-3 py-2 text-[14px]"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{c.periodLabel}</span>
            {c.periodStart ? <span className="text-ink-2">с {date(c.periodStart)}</span> : null}
            {c.cell ? <Badge tone="warning">ячейка {c.cell}</Badge> : null}
          </div>
          <p className="num mt-1">
            было {tenge(c.was)} → стало {tenge(c.became)} <b>({signed(c.delta)})</b>
          </p>
          <p className="mt-1 text-ink-2">{c.reason}</p>
        </li>
      ))}
    </ul>
  );
}

function Result({ r }: { r: BudgetReconciliation }) {
  const off = r.weeks.filter((w) => w.status !== 'ok');
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div
        className={
          r.matches
            ? 'rounded-md bg-success-soft px-4 py-3 text-[14px]'
            : 'rounded-md bg-danger-soft px-4 py-3 text-[14px]'
        }
      >
        <p className="font-medium">
          {r.matches
            ? `«${r.fileName}» сходится с версией «${r.versionName}» до тенге`
            : `«${r.fileName}» расходится с версией «${r.versionName}»`}
        </p>
        <p className="num mt-1">
          Строк сверено {r.linesChecked}, совпало {r.linesMatched}. Итог файла {tenge(r.totals.file)}, по
          разделам файла {tenge(r.totals.fileLines)}, в портале {tenge(r.totals.portal)}; по неделям: файл{' '}
          {tenge(r.totals.fileDistributed)}, портал {tenge(r.totals.portalDistributed)}.
        </p>
        <p className="mt-1 text-ink-2">
          Сверяются суммы, загруженные из файла. Выбранные варианты исполнения и утверждённые изменения
          бюджета меняют итог раздела на вкладке, но в файле их нет — в сверку они не входят.
        </p>
      </div>

      {r.lines.length ? (
        <div>
          <p className="mb-2 font-medium">Строки, которые расходятся</p>
          <ul className="flex flex-col gap-2">
            {r.lines.map((l, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: у строк портала нет своего номера в файле
                key={i}
                className="rounded-md border border-border px-3 py-2 text-[14px]"
              >
                <p className="font-medium">
                  {l.code ? `${l.code}. ` : ''}
                  {l.name}
                  <span className="ml-2 font-normal text-ink-2">
                    {l.row
                      ? `строка ${l.row} файла`
                      : l.addedInPortal && l.kind === 'alternative'
                        ? 'вариант добавлен в портале — не расхождение'
                        : 'нет в файле'}
                  </span>
                </p>
                <p className="num mt-1">
                  в файле {tenge(l.fileAmount)}, в портале{' '}
                  {l.portalAmount === null ? 'нет' : tenge(l.portalAmount)}
                </p>
                {l.weeks.length ? (
                  <p className="mt-1 text-ink-2">
                    Недели расходятся: {l.weeks.map((w) => date(w)).join(', ')}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {off.length ? (
        <div>
          <p className="mb-2 font-medium">Недели, где итог файла не равен порталу</p>
          <ul className="flex flex-col gap-2">
            {off.map((w, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: недели файла могут повторяться
              <li key={i} className="rounded-md border border-border px-3 py-2 text-[14px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{w.label}</span>
                  {w.status === 'corrected' ? (
                    <Badge tone="warning">итог файла исправлен</Badge>
                  ) : (
                    <Badge tone="danger">расходится</Badge>
                  )}
                </div>
                <p className="num mt-1">
                  итог файла {tenge(w.fileTotal)}, по строкам файла {tenge(w.fileLines)}, в портале{' '}
                  {tenge(w.portal)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {r.corrections.length ? (
        <div>
          <p className="mb-2 font-medium">Исправления итогов этого файла</p>
          <Corrections items={r.corrections} />
        </div>
      ) : null}
    </div>
  );
}

export function ReconcilePanel({ versionId }: { versionId: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const journal = useQuery({
    queryKey: ['budget', 'corrections', versionId],
    queryFn: () => api<BudgetCorrectionsView>(`/budget-versions/${versionId}/corrections`),
  });
  const m = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<BudgetReconciliation>(`/budget-versions/${versionId}/reconcile`, { form });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const items = journal.data?.corrections ?? [];
  const noJournal = journal.isSuccess && journal.data.corrections === null;
  // Результат сверки — только для показанной версии
  const result = m.data?.versionId === versionId ? m.data : null;
  return (
    <Panel
      title="Сверка с файлом"
      meta="ПМ-01: суммы портала и файла Excel до тенге; исправления итогов файла — «было → стало»"
      actions={
        <>
          <input
            ref={ref}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) m.mutate(f);
              e.target.value = '';
            }}
          />
          <Button
            variant="secondary"
            icon={<FileSearch className="size-4" />}
            loading={m.isPending}
            onClick={() => ref.current?.click()}
          >
            Сверить с файлом
          </Button>
        </>
      }
    >
      <ErrorBox error={journal.error} />
      {items.length ? (
        <>
          <p className="mb-2 text-[14px] text-ink-2">
            При загрузке{journal.data?.fileName ? ` «${journal.data.fileName}»` : ''} итоги недель файла не
            совпали с суммой разделов. Портал считает итоги по строкам; исправления записаны здесь:
          </p>
          <Corrections items={items} />
        </>
      ) : noJournal ? (
        <p className="text-[14px] text-ink-2">
          Эта версия загружена раньше, чем портал начал вести журнал исправлений. Выберите тот же файл Excel —
          сверка покажет, какие итоги файла портал исправил по строкам.
        </p>
      ) : journal.isSuccess ? (
        <p className="text-[14px] text-ink-2">
          Исправлений итогов файла при загрузке этой версии не было. Выберите файл Excel, чтобы сверить с ним
          суммы версии построчно и по неделям.
        </p>
      ) : null}
      {result ? <Result r={result} /> : null}
    </Panel>
  );
}
